import type { ClientEvent, EventBatch, EventResponse } from "../types/tracking";
import { readStorage, writeStorage } from "./safe-storage";

const TRACKER_URL = "/event";
const METRIKA_ID = 92514939;
const SESSION_TTL_MS = 30 * 60 * 1000;
const BATCH_INTERVAL_MS = 2000;
const MAX_QUEUE_SIZE = 100;
const RETRY_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const RAGE_CLICK_COUNT = 3;
const RAGE_CLICK_WINDOW_MS = 1000;
const ENGAGED_THRESHOLD = 2;
const IDLE_TIMEOUT_MS = 30_000;
const ENGAGED_SAMPLE_MS = 1_000;
const ENGAGED_GOAL_THRESHOLD_MS = 30_000;
const TIME_ON_PAGE_CAP_S = 1800;

const SESSION_KEY = "_lc_session";
const SESSION_COOKIE_NAME = "_lc_sid";
const UUID_V4_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ATTRIBUTION_KEY = "_lc_attribution";

const queue: ClientEvent[] = [];
let retryQueue: EventBatch[] = [];
let visitNumber = 0;
let returnVisitFired = false;
let sectionViewCount = 0;
let pageEngagedFired = false;
let engaged30Fired = false;
let initialized = false;
let backgroundFlushArmed = false;
let initTs = 0;
let checkoutOpenTs = 0;
let formStartTs = 0;
let hiddenSince = 0;
let beaconSent = false;
let idbAvailable = true;
let engagedMs = 0;
let lastActivityTs = 0;
let lastPageUrl = "";
let maxScrollPct = 0;

interface ErrorEvent {
  error_type: string;
  message: string;
  stack?: string;
  page_url: string;
  metadata?: Record<string, unknown>;
}

const errorQueue: ErrorEvent[] = [];
const recentErrors = new Set<string>();

// The browser masks cross-origin (third-party, no-CORS) script exceptions as a
// content-free sentinel: { message: "Script error.", filename: "", lineno: 0,
// error: null } — no stack, no source, nothing to debug. They come from
// third-party scripts we can't fix (Metrika, Jivo, Clarity). Anchored so a real
// error that merely contains the phrase still reports.
const CROSS_ORIGIN_SCRIPT_ERROR = /^Script error\.?$/;

function isOwnSource(filename?: string): boolean {
  // An empty/unknown filename is the signature of the cross-origin sentinel
  // above, so treat unknown source as NOT ours. (This used to `return true`,
  // which forwarded every masked third-party "Script error." to /errors → the
  // infra-alert as service=frontend — exactly the noise this guard exists to
  // drop.)
  if (!filename) return false;
  return filename.includes(location.hostname) || filename.startsWith("/");
}

// Injected/eval'd third-party code (browser extensions, in-app mobile WebViews)
// throws with a stack whose frames are all `<anonymous>` and reference nothing
// on our origin — e.g. the Android-WebView "XMLHttpRequest is not a constructor"
// noise, whose window.onerror `filename` the browser mis-attributes to our
// same-origin page-entry bundle (so isOwnSource passes and it pages the team as
// service=frontend). Our own code can NEVER produce such a stack: the app-page
// CSP forbids eval/new Function, so every first-party frame carries its
// same-origin URL. A stack that exists but touches nothing on our origin is
// therefore not ours.
function isForeignInjectedStack(stack?: string): boolean {
  return !!stack && !stack.includes(location.hostname);
}

// Vike's client router (vike-react Client Routing) asserts that every
// window.history entry carries its own { vike } marker (vike history.js →
// assertIsEnhanced → assert(false, { state })). On ad-traffic pages that BOTH
// use hash-anchor CTAs (#pricing/#faq → popstate) AND run a deferred analytics
// script that monkey-patches history.replaceState AFTER Vike (Yandex Metrika
// strips yclid from the URL with a null state), Vike is no longer the last
// patcher in the chain, so its next history read finds state===null and throws
// `[vike][Bug] … {"state":null}`. Vike self-heals (microtask re-patch #2504 +
// popstate hard-reload fallback #2801) and the browser handles the #hash scroll
// natively, so the user flow completes — this is an upstream-owned false
// positive on ad traffic, not an actionable app bug (same class as
// sveltejs/kit#11671, a GA-monkey-patch false positive). The stack is our
// same-origin vike bundle, so isOwnSource/isForeignInjectedStack both pass it —
// hence the explicit drop. Scoped tightly to the { state } assertion (the vike
// bug tag + the `"state":` debug marker) so any OTHER Vike bug still pages.
const VIKE_BUG_TAG = "You stumbled upon a Vike bug";
function isVikeHistoryStateBug(message?: string): boolean {
  return !!message && message.includes(VIKE_BUG_TAG) && message.includes('"state":');
}

// Whether a window `error` event is worth reporting to /errors: skip the
// cross-origin sentinel, the Vike history-state false positive, injected/
// extension stacks, and anything sourced outside our own origin. Exported for
// the regression test (tests/unit/tracking-error-filter.test.ts).
export function shouldReportErrorEvent(
  message: string,
  filename?: string,
  stack?: string,
): boolean {
  if (CROSS_ORIGIN_SCRIPT_ERROR.test(message)) return false;
  if (isVikeHistoryStateBug(message)) return false;
  if (isForeignInjectedStack(stack)) return false;
  return isOwnSource(filename);
}

function queueError(err: ErrorEvent): void {
  const key = `${err.error_type}:${err.message}:${err.metadata?.line || ""}`;
  if (recentErrors.has(key)) return;
  recentErrors.add(key);
  if (recentErrors.size > 50) recentErrors.clear();
  errorQueue.push(err);
  flushErrors();
}

async function flushErrors(): Promise<void> {
  if (errorQueue.length === 0) return;
  const errors = errorQueue.splice(0);
  const session = getSession();
  const body = {
    visitor_id: getVisitorId(),
    session_id: session.id,
    errors,
  };
  try {
    await fetch("/errors", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    });
  } catch {
    try {
      navigator.sendBeacon(
        "/errors",
        new Blob([JSON.stringify(body)], { type: "application/json" }),
      );
    } catch {}
  }
}

function setupErrorHandlers(): void {
  window.addEventListener("error", (e) => {
    if (!shouldReportErrorEvent(e.message, e.filename, e.error?.stack)) return;
    queueError({
      error_type: "js_error",
      message: e.message,
      stack: e.error?.stack?.slice(0, 2000),
      page_url: location.href,
      metadata: { source: e.filename, line: e.lineno, col: e.colno },
    });
  });

  window.addEventListener("unhandledrejection", (e) => {
    const reason = e.reason;
    // Vike's async popstate handler rejects with the history-state assertion
    // (see isVikeHistoryStateBug) — drop it here too, not just on window.error.
    if (isVikeHistoryStateBug(reason?.message)) return;
    if (isForeignInjectedStack(reason?.stack)) return;
    queueError({
      error_type: "unhandled_rejection",
      message: reason?.message || String(reason).slice(0, 1000),
      stack: reason?.stack?.slice(0, 2000),
      page_url: location.href,
    });
  });
}

interface Attribution {
  yclid?: string;
  gclid?: string;
  fbclid?: string;
  etext?: string;
  ybaip?: string;
  utm?: {
    source?: string;
    medium?: string;
    campaign?: string;
    content?: string;
    term?: string;
  };
}

interface SessionData {
  id: string;
  ts: number;
}

interface SectionState {
  enterTs: number;
  viewFired: boolean;
  viewTimer: ReturnType<typeof setTimeout> | null;
}

const sectionStates = new Map<Element, SectionState>();
const clickTimestamps = new Map<EventTarget, number[]>();

export function parseAttributionFromUrl(url: string): Attribution {
  let params: URLSearchParams;
  try {
    params = new URL(url).searchParams;
  } catch {
    return {};
  }

  const result: Attribution = {};

  const clickIds = ["yclid", "gclid", "fbclid", "etext", "ybaip"] as const;
  for (const key of clickIds) {
    const val = params.get(key);
    if (val) {
      result[key] = val;
    }
  }

  const utmKeys = ["source", "medium", "campaign", "content", "term"] as const;
  const utm: NonNullable<Attribution["utm"]> = {};
  let hasUtm = false;
  for (const key of utmKeys) {
    const val = params.get(`utm_${key}`);
    if (val) {
      utm[key] = val;
      hasUtm = true;
    }
  }
  if (hasUtm) {
    result.utm = utm;
  }

  return result;
}

export function isSessionExpired(ts: number): boolean {
  return Date.now() - ts >= SESSION_TTL_MS;
}

function generateId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function getVisitorId(): string {
  if (typeof document === "undefined") return "";
  const match = document.cookie.match(/(?:^|;\s*)_lc_vid=([^;]+)/);
  if (match) return match[1];
  const tempId = generateId();
  return tempId;
}

export function readClickId(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const raw = new URLSearchParams(window.location.search).get("lc_cid");
  return raw && UUID_V4_RE.test(raw) ? raw : undefined;
}

function getSession(): SessionData {
  // Prefer the server-owned `_lc_sid` cookie so JS-side events share the same
  // session_id as the SSR-written page_view (see server/lib/ssr-pageview.ts).
  // A present cookie is always non-expired (the browser drops it past maxAge).
  if (typeof document !== "undefined") {
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${SESSION_COOKIE_NAME}=([^;]+)`));
    if (match && UUID_V4_RE.test(match[1])) {
      const session: SessionData = { id: match[1], ts: Date.now() };
      writeStorage("local", SESSION_KEY, JSON.stringify(session));
      return session;
    }
  }
  try {
    const raw = readStorage("local", SESSION_KEY);
    if (raw) {
      const data = JSON.parse(raw) as SessionData;
      if (!isSessionExpired(data.ts)) {
        data.ts = Date.now();
        writeStorage("local", SESSION_KEY, JSON.stringify(data));
        return data;
      }
    }
  } catch {}
  const session: SessionData = { id: generateId(), ts: Date.now() };
  writeStorage("local", SESSION_KEY, JSON.stringify(session));
  return session;
}

// Dynamic-headline reward attribution (ADR-0069). The SSR resolver sets a
// server-owned `_lc_hv` cookie (`clusterId.variantId.epoch`, httpOnly:false) when
// a headline variant is assigned. Tagging queued events with `{cluster, variant}`
// is the bandit's reward signal: the hourly alloc job (gc-sync
// POST /sync/headline-alloc) counts `form_submit` / `registration_submit` /
// `page_engaged` per `event_data->>'variant'`. The cookie exists only on
// headline-active pages, so events elsewhere are untouched.
// See server/lib/headline-select.ts + docs/specs/website/dynamic-headline-bandit.md.
export function parseHeadlineCookie(
  raw: string | undefined | null,
): { cluster: string; variant: number } | null {
  if (!raw) return null;
  const parts = raw.split(".");
  if (parts.length !== 3) return null;
  const [cluster, variantStr] = parts;
  if (!cluster || !/^\d+$/.test(variantStr)) return null;
  const variant = Number(variantStr);
  if (!Number.isSafeInteger(variant)) return null;
  return { cluster, variant };
}

export function getHeadlineAssignment(): { cluster: string; variant: number } | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(/(?:^|;\s*)_lc_hv=([^;]+)/);
  return match ? parseHeadlineCookie(decodeURIComponent(match[1])) : null;
}

export function tagEventDataWithHeadline(
  data: Record<string, unknown>,
  assignment: { cluster: string; variant: number } | null,
): Record<string, unknown> {
  if (!assignment || data.variant !== undefined) return data;
  return { ...data, cluster: assignment.cluster, variant: assignment.variant };
}

function getAttribution(): Attribution {
  const fromUrl = parseAttributionFromUrl(window.location.href);
  if (Object.keys(fromUrl).length > 0) {
    writeStorage("local", ATTRIBUTION_KEY, JSON.stringify(fromUrl));
    return fromUrl;
  }

  try {
    const stored = readStorage("local", ATTRIBUTION_KEY);
    if (stored) return JSON.parse(stored) as Attribution;
  } catch {}

  return {};
}

export interface CheckoutAttribution {
  yclid?: string;
  gclid?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
}

export function getCheckoutAttribution(): CheckoutAttribution {
  if (typeof window === "undefined") return {};
  const a = getAttribution();
  const out: CheckoutAttribution = {};
  if (a.yclid) out.yclid = a.yclid;
  if (a.gclid) out.gclid = a.gclid;
  if (a.utm?.source) out.utm_source = a.utm.source;
  if (a.utm?.medium) out.utm_medium = a.utm.medium;
  if (a.utm?.campaign) out.utm_campaign = a.utm.campaign;
  if (a.utm?.content) out.utm_content = a.utm.content;
  if (a.utm?.term) out.utm_term = a.utm.term;
  return out;
}

function buildPageGeometry(): {
  page_height: number;
  viewport_height: number;
  sections: { type: string; offset_top: number; height: number }[];
} {
  const sections: { type: string; offset_top: number; height: number }[] = [];
  const els = document.querySelectorAll("[data-section]");
  for (const el of els) {
    const type = el.getAttribute("data-section");
    if (!type) continue;
    const rect = el.getBoundingClientRect();
    sections.push({
      type,
      offset_top: Math.round(rect.top + window.scrollY),
      height: Math.round(rect.height),
    });
  }
  return {
    page_height: document.documentElement.scrollHeight,
    viewport_height: window.innerHeight,
    sections,
  };
}

// Shared by the pricing_view goal (scroll depth at the moment pricing enters
// view) and the running max-scroll-depth tracked for page_leave. A page
// shorter than the viewport has nothing left to scroll — treat it as 100%
// rather than dividing by a non-positive scrollable height.
function getScrollDepthPct(): number {
  const scrollable = document.documentElement.scrollHeight - window.innerHeight;
  if (scrollable <= 0) return 100;
  const pct = (window.scrollY / scrollable) * 100;
  return Math.min(100, Math.max(0, Math.round(pct)));
}

// tracking.ts is bundled TWICE: the standalone /tracking.js bundle (built by
// scripts/build-tracking.ts) is the only copy `initTracking()` ever runs on —
// it owns the 2s setInterval(flushBatch) + pagehide/visibilitychange
// sendBeacon. Components import trackFormStart/trackFormFieldFocus/
// trackFormFieldError/trackRegistrationSubmit/trackFormSubmit from
// "@/utils/tracking" in the APP bundle, a separate, never-initialized module
// instance whose `queue` had no flush timer and no unload handler — those
// events shipped only when a submit called flushNow(), so every form
// abandoner's events were silently lost. Arming a lazy background flush on
// first queueEvent() closes that gap for the uninitialized copy; it is a
// no-op in the already-initialized standalone copy.
function armBackgroundFlush(): void {
  if (backgroundFlushArmed || typeof window === "undefined") return;
  backgroundFlushArmed = true;
  setInterval(() => {
    flushBatch();
  }, BATCH_INTERVAL_MS);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushNow();
  });
  window.addEventListener("pagehide", () => {
    flushNow();
  });
}

function queueEvent(type: string, data: Record<string, unknown> = {}): void {
  if (!initialized) armBackgroundFlush();
  const eventData = tagEventDataWithHeadline(data, getHeadlineAssignment());
  queue.push({ type, ts: Date.now(), data: eventData });
  if (queue.length >= MAX_QUEUE_SIZE) {
    flushBatch();
  }
}

function reachGoal(goalId: string, params?: Record<string, unknown>): void {
  if (typeof window !== "undefined" && typeof window.ym === "function") {
    const abVariants = (window as { __lc_ab?: Record<string, string> }).__lc_ab;
    const mergedParams = abVariants ? { ...params, ab_variant: abVariants } : params;
    window.ym(METRIKA_ID, "reachGoal", goalId, mergedParams);
  }
}

function buildBatch(): EventBatch | null {
  if (queue.length === 0) return null;

  const events = queue.splice(0);
  const session = getSession();
  const attribution = getAttribution();

  const batch: EventBatch = {
    visitor_id: getVisitorId(),
    session_id: session.id,
    page_url: window.location.href,
    events,
  };

  if ((window as { __lc_ab?: Record<string, string> }).__lc_ab) {
    batch.ab = (window as { __lc_ab?: Record<string, string> }).__lc_ab;
  }

  // The collector stamps referrer/yclid/gclid/fbclid/etext/ybaip/utm onto
  // EVERY row of the batch server-side (and its users.source_utm update is
  // idempotent), so attribution must ride on EVERY batch, not just a
  // session's first — a once-per-session dedup here left every later event
  // (form_start/form_submit/registration_submit/page_leave/...) with NULL
  // utm_* columns, which is what production showed for every visitor's
  // post-first-batch events, Yandex Direct traffic included.
  if (document.referrer) batch.referrer = document.referrer;
  if (attribution.yclid) batch.yclid = attribution.yclid;
  if (attribution.gclid) batch.gclid = attribution.gclid;
  if (attribution.fbclid) batch.fbclid = attribution.fbclid;
  if (attribution.etext) batch.etext = attribution.etext;
  if (attribution.ybaip) batch.ybaip = attribution.ybaip;
  if (attribution.utm) batch.utm = attribution.utm;

  return batch;
}

async function persistRetry(batch: EventBatch): Promise<void> {
  retryQueue.push(batch);
  if (retryQueue.length > MAX_QUEUE_SIZE) {
    retryQueue = retryQueue.slice(-MAX_QUEUE_SIZE);
  }

  if (!idbAvailable) return;

  try {
    const { set } = await import("idb-keyval");
    await set("_lc_retry_queue", retryQueue);
  } catch {
    idbAvailable = false;
  }
}

async function loadRetryQueue(): Promise<void> {
  if (!idbAvailable) return;
  try {
    const { get } = await import("idb-keyval");
    const stored = await get<EventBatch[]>("_lc_retry_queue");
    if (Array.isArray(stored)) {
      const cutoff = Date.now() - RETRY_MAX_AGE_MS;
      retryQueue = stored.filter((b) => b.events.length > 0 && b.events[0].ts > cutoff);
    }
  } catch {
    idbAvailable = false;
  }
}

async function clearRetryQueue(): Promise<void> {
  retryQueue = [];
  if (!idbAvailable) return;
  try {
    const { del } = await import("idb-keyval");
    await del("_lc_retry_queue");
  } catch {
    idbAvailable = false;
  }
}

async function flushBatch(): Promise<void> {
  const batch = buildBatch();
  const pending = retryQueue.splice(0);
  if (batch) pending.push(batch);
  if (pending.length === 0) return;

  for (const item of pending) {
    try {
      const res = await fetch(TRACKER_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(item),
        keepalive: true,
      });

      if (res.ok) {
        try {
          const data = (await res.json()) as EventResponse;
          visitNumber = data.visit_number;
          if (visitNumber > 1 && !returnVisitFired) {
            returnVisitFired = true;
            queueEvent("return_visit", { visit_number: visitNumber });
          }
        } catch {}
        await clearRetryQueue();
      } else if (res.status !== 422) {
        await persistRetry(item);
      }
    } catch {
      await persistRetry(item);
    }
  }
}

// Immediately ship whatever is queued, without waiting for the 2s batch timer.
// Conversion events (registration_submit / form_submit) are fired right before a
// redirect (the form navigates to a thank-you page or payment provider on
// success), so the queued event would otherwise be lost when the page unloads
// before the next scheduled flush. sendBeacon is purpose-built to survive
// navigation (same path measureWebVitals already uses successfully); fetch +
// keepalive is the fallback when sendBeacon is unavailable or its queue is full.
function flushNow(): void {
  if (typeof window === "undefined") return;
  const batch = buildBatch();
  if (!batch) return;
  const body = JSON.stringify(batch);
  try {
    if (
      typeof navigator !== "undefined" &&
      typeof navigator.sendBeacon === "function" &&
      navigator.sendBeacon(TRACKER_URL, new Blob([body], { type: "application/json" }))
    ) {
      return;
    }
  } catch {}
  // Fallback: keepalive fetch also outlives the navigation.
  try {
    void fetch(TRACKER_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {}
}

function sendBeacon(): void {
  if (beaconSent) return;
  beaconSent = true;

  const headlineAssignment = getHeadlineAssignment();

  for (const [el, state] of sectionStates) {
    if (state.enterTs > 0) {
      const type = el.getAttribute("data-section") || "unknown";
      queue.push({
        type: "section_leave",
        ts: Date.now(),
        data: tagEventDataWithHeadline(
          {
            section_type: type,
            enter_ts: state.enterTs,
            duration_ms: Date.now() - state.enterTs,
          },
          headlineAssignment,
        ),
      });
    }
  }
  if (checkoutOpenTs > 0) {
    queue.push({
      type: "checkout_abandon",
      ts: Date.now(),
      data: tagEventDataWithHeadline(
        { duration_ms: Date.now() - checkoutOpenTs },
        headlineAssignment,
      ),
    });
  }

  queue.push({
    type: "page_leave",
    ts: Date.now(),
    data: tagEventDataWithHeadline(
      {
        time_on_page_s: Math.min(TIME_ON_PAGE_CAP_S, Math.round((Date.now() - initTs) / 1000)),
        engaged_time_s: Math.round(engagedMs / 1000),
        sections_viewed: sectionViewCount,
        max_scroll_pct: Math.max(maxScrollPct, getScrollDepthPct()),
      },
      headlineAssignment,
    ),
  });

  const batch = buildBatch();
  if (!batch) return;

  try {
    navigator.sendBeacon(
      TRACKER_URL,
      new Blob([JSON.stringify(batch)], { type: "application/json" }),
    );
  } catch {}
}

function setupIntersectionObserver(): void {
  if (typeof IntersectionObserver === "undefined") return;
  const elements = document.querySelectorAll("[data-section]");
  if (elements.length === 0) return;

  const enterObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const el = entry.target;
        const name = el.getAttribute("data-section") || "unknown";
        const role = el.getAttribute("data-section-role") || "unknown";
        const id = el.getAttribute("data-section-id") || undefined;
        const baseAttrs = { section_name: name, section_role: role, section_id: id };

        if (entry.isIntersecting) {
          const state: SectionState = {
            enterTs: Date.now(),
            viewFired: false,
            viewTimer: null,
          };

          queueEvent("section_enter", {
            ...baseAttrs,
            section_type: name,
            scroll_y: Math.round(window.scrollY),
            page_height: document.documentElement.scrollHeight,
            viewport_height: window.innerHeight,
          });

          if (role === "pricing") {
            const timeOnPage = (Date.now() - initTs) / 1000;
            reachGoal("pricing_view", {
              scroll_depth_pct: getScrollDepthPct(),
              time_on_page_s: Math.round(timeOnPage),
            });
          }

          const sectionHeight = el.getBoundingClientRect().height;
          const viewThreshold = Math.min(0.5, 300 / sectionHeight);

          state.viewTimer = setTimeout(() => {
            if (document.visibilityState === "hidden") return;
            const rect = el.getBoundingClientRect();
            const visibleRatio = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
            const ratio = visibleRatio / rect.height;

            if (ratio >= viewThreshold && !state.viewFired) {
              state.viewFired = true;
              sectionViewCount++;
              queueEvent("section_view", {
                ...baseAttrs,
                section_type: name,
                duration_ms: Date.now() - state.enterTs,
              });

              if (sectionViewCount >= ENGAGED_THRESHOLD && !pageEngagedFired) {
                pageEngagedFired = true;
                const timeOnPage = (Date.now() - initTs) / 1000;
                const engagedTime = engagedMs / 1000;
                reachGoal("page_engaged", {
                  sections_viewed: sectionViewCount,
                  time_on_page_s: Math.round(timeOnPage),
                  engaged_time_s: Math.round(engagedTime),
                });
                queueEvent("page_engaged", {
                  sections_viewed: sectionViewCount,
                  time_on_page_s: Math.round(timeOnPage),
                  engaged_time_s: Math.round(engagedTime),
                });
              }
            }
          }, 1000);

          sectionStates.set(el, state);
        } else {
          const state = sectionStates.get(el);
          if (state) {
            if (state.viewTimer) clearTimeout(state.viewTimer);
            queueEvent("section_leave", {
              ...baseAttrs,
              section_type: name,
              enter_ts: state.enterTs,
              duration_ms: Date.now() - state.enterTs,
            });
            sectionStates.delete(el);
          }
        }
      }
    },
    { threshold: 0 },
  );

  for (const el of elements) {
    enterObserver.observe(el);
  }
}

function setupClickDelegation(): void {
  document.addEventListener("click", (e) => {
    const target = e.target as Element | null;
    if (!target) return;

    const tracked = target.closest("[data-track]");
    if (tracked) {
      const trackValue = tracked.getAttribute("data-track") || "";
      const section = tracked.closest("[data-section]");
      const sectionName = section?.getAttribute("data-section") || "unknown";
      queueEvent("section_interact", {
        target: trackValue,
        section_name: sectionName,
        section_role: section?.getAttribute("data-section-role") || "unknown",
        section_id: section?.getAttribute("data-section-id") || undefined,
        section_type: sectionName,
      });
    }

    const now = Date.now();
    const timestamps = clickTimestamps.get(target) || [];
    timestamps.push(now);

    const recent = timestamps.filter((t) => now - t < RAGE_CLICK_WINDOW_MS);
    clickTimestamps.set(target, recent);

    if (recent.length >= RAGE_CLICK_COUNT) {
      queueEvent("rage_click", {
        element:
          (target as HTMLElement).dataset?.track ||
          (target as HTMLElement).tagName?.toLowerCase() ||
          "unknown",
        count: recent.length,
      });
      clickTimestamps.set(target, []);
    }
  });
}

function setupUnloadHandlers(): void {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      hiddenSince = Date.now();
      sendBeacon();
    } else if (hiddenSince > 0) {
      const away = Date.now() - hiddenSince;
      hiddenSince = 0;
      beaconSent = false;
      initTs += away;
      lastActivityTs = Date.now();
      if (checkoutOpenTs > 0) checkoutOpenTs += away;
      if (formStartTs > 0) formStartTs += away;
      for (const state of sectionStates.values()) {
        state.enterTs += away;
      }
    }
  });

  window.addEventListener("pagehide", () => {
    sendBeacon();
  });
}

function setupEngagedTimeTracking(): void {
  const markActive = (): void => {
    lastActivityTs = Date.now();
  };
  const events = ["mousedown", "mousemove", "scroll", "touchstart", "keydown", "wheel"] as const;
  for (const ev of events) {
    window.addEventListener(ev, markActive, { passive: true, capture: true });
  }

  setInterval(() => {
    if (document.visibilityState !== "visible") return;
    if (typeof document.hasFocus === "function" && !document.hasFocus()) return;
    if (Date.now() - lastActivityTs >= IDLE_TIMEOUT_MS) return;
    engagedMs += ENGAGED_SAMPLE_MS;
    // Direct optimization proxy (ADR: engaged-session goal). Registrations are too
    // sparse (~14/wk) to train the auto-strategy; a 30s active-attention non-bounce
    // fires ~99/wk and predicts registration at 6.5x lift, so it feeds the composite
    // proxy goal (engaged_30s OR form_start). Fires once per page view.
    if (!engaged30Fired && engagedMs >= ENGAGED_GOAL_THRESHOLD_MS) {
      engaged30Fired = true;
      reachGoal("engaged_30s", { engaged_time_s: Math.round(engagedMs / 1000) });
    }
  }, ENGAGED_SAMPLE_MS);
}

function setupScrollDepthTracking(): void {
  const updateMaxScroll = (): void => {
    maxScrollPct = Math.max(maxScrollPct, getScrollDepthPct());
  };
  window.addEventListener("scroll", updateMaxScroll, { passive: true, capture: true });
  updateMaxScroll();
}

function setupOnlineHandler(): void {
  window.addEventListener("online", () => {
    if (retryQueue.length > 0) {
      flushBatch();
    }
  });
}

// Build the single-event batch for one Core Web Vital. One row PER metric so
// each can be delivered independently. `attribution` carries optional diagnostic
// fields (the element selector + sub-part phases for LCP/INP/CLS) merged into the
// event data; undefined keys are dropped by JSON.stringify. Exported for the test.
export function buildPerfMetricBatch(
  metric: string,
  value: number,
  attribution?: Record<string, unknown>,
): EventBatch {
  const session = getSession();
  return {
    visitor_id: getVisitorId(),
    session_id: session.id,
    page_url: window.location.href,
    events: [
      {
        type: "page_performance",
        ts: Date.now(),
        data: attribution ? { metric, value, ...attribution } : { metric, value },
      },
    ],
  };
}

function reportWebVital(
  metric: string,
  value: number,
  attribution?: Record<string, unknown>,
): void {
  try {
    navigator.sendBeacon(
      TRACKER_URL,
      new Blob([JSON.stringify(buildPerfMetricBatch(metric, value, attribution))], {
        type: "application/json",
      }),
    );
  } catch {}
}

// Cap an attribution selector so a deep DOM path can't bloat the 8 KB-capped
// collector event_data (the collector rejects an oversized event).
function perfTarget(selector: string | undefined): string | undefined {
  if (!selector) return undefined;
  return selector.length > 256 ? selector.slice(0, 256) : selector;
}

async function measureWebVitals(): Promise<void> {
  // Ship each metric via its OWN beacon the moment web-vitals reports its final
  // value — TTFB/FCP at load, LCP/CLS/INP at first interaction or page-hide.
  // The previous implementation queued ONE page_performance event only after
  // LCP, CLS AND INP had all reported. INP never fires for a visitor who
  // bounces without interacting, so the whole event (including LCP) was dropped
  // for exactly the bounce-heavy ad traffic we most need to measure. Reporting
  // each metric independently — and via sendBeacon, not the batched queue whose
  // unload flush may already have fired — means none can be lost.
  //
  // The ATTRIBUTION build adds, per metric, the element selector + the sub-part
  // phase breakdown so the field data says WHICH element and WHICH phase to
  // optimize (not just a number): LCP target/url + ttfb/load-delay/load-duration/
  // render-delay, INP target/type + input-delay/processing/presentation, CLS
  // largest-shift target/value. Standard `web-vitals` exposes no `attribution`.
  try {
    const { onCLS, onFCP, onINP, onLCP, onTTFB } = await import("web-vitals/attribution");
    onTTFB((m) => reportWebVital("ttfb", Math.round(m.value)));
    onFCP((m) => reportWebVital("fcp", Math.round(m.value)));
    onLCP((m) => {
      const a = m.attribution;
      reportWebVital("lcp", Math.round(m.value), {
        element: perfTarget(a.target),
        url: a.url || undefined,
        ttfb: Math.round(a.timeToFirstByte),
        load_delay: Math.round(a.resourceLoadDelay),
        load_duration: Math.round(a.resourceLoadDuration),
        render_delay: Math.round(a.elementRenderDelay),
      });
    });
    onCLS((m) => {
      const a = m.attribution;
      reportWebVital("cls", Math.round(m.value * 1000) / 1000, {
        element: perfTarget(a.largestShiftTarget),
        largest_shift:
          a.largestShiftValue != null ? Math.round(a.largestShiftValue * 1000) / 1000 : undefined,
      });
    });
    onINP((m) => {
      const a = m.attribution;
      reportWebVital("inp", Math.round(m.value), {
        element: perfTarget(a.interactionTarget),
        interaction_type: a.interactionType,
        input_delay: Math.round(a.inputDelay),
        processing: Math.round(a.processingDuration),
        presentation: Math.round(a.presentationDelay),
      });
    });
  } catch {}
}

export function isPageViewWrittenBySsr(): boolean {
  if (typeof document === "undefined") return false;
  const meta = document.querySelector<HTMLMetaElement>('meta[name="lc-ssr-pageview"]');
  return meta?.content === "1";
}

export function initTracking(): void {
  if (typeof window === "undefined" || initialized) return;
  if (window.location.hostname !== "lenacareer.ru") return;
  initialized = true;

  initTs = Date.now();
  lastActivityTs = Date.now();
  engagedMs = 0;
  sectionViewCount = 0;
  pageEngagedFired = false;
  engaged30Fired = false;
  checkoutOpenTs = 0;
  maxScrollPct = 0;

  getSession();
  getAttribution();

  lastPageUrl = window.location.href;
  const geometry = buildPageGeometry();
  if (!isPageViewWrittenBySsr()) {
    // The SSR page_view normally carries `click_id` (ADR-0129), but the pages
    // that land here are exactly the ones its denylist skips — `/l/thank-you`
    // among them, which is where the DOI delivery email's own links point. Read
    // it client-side so those landings still join to the click that made them.
    const clickId = readClickId();
    queueEvent("page_view", {
      ...geometry,
      url: window.location.href,
      referrer: document.referrer || undefined,
      ...(clickId ? { click_id: clickId } : {}),
    });
  }

  (window as { __lcTrackPageView?: () => void }).__lcTrackPageView = trackClientPageView;

  loadRetryQueue();

  setupErrorHandlers();
  setupIntersectionObserver();
  setupClickDelegation();
  setupUnloadHandlers();
  setupEngagedTimeTracking();
  setupScrollDepthTracking();
  setupOnlineHandler();

  setInterval(() => {
    flushBatch();
  }, BATCH_INTERVAL_MS);

  measureWebVitals();
}

export function trackClientPageView(): void {
  if (typeof window === "undefined" || !initialized) return;
  if (window.location.hostname !== "lenacareer.ru") return;

  const url = window.location.href;
  if (url === lastPageUrl) return;
  const previousUrl = lastPageUrl;
  lastPageUrl = url;

  initTs = Date.now();
  lastActivityTs = Date.now();
  engagedMs = 0;
  sectionViewCount = 0;
  pageEngagedFired = false;
  engaged30Fired = false;
  maxScrollPct = 0;

  const geometry = buildPageGeometry();
  queueEvent("page_view", {
    ...geometry,
    url,
    referrer: previousUrl || document.referrer || undefined,
    source: "js",
  });
}

export function trackCheckoutOpen(tier: string): void {
  checkoutOpenTs = Date.now();
  queueEvent("checkout_open", { tier, visit_number: visitNumber });
  reachGoal("checkout_open", { tier, visit_number: visitNumber });
}

/**
 * A page can render several independent form instances (the guide pages carry a
 * hero form AND a bottom form), and every friction event they emit was
 * previously indistinguishable — same `field` kinds, same `page_url`, same
 * `event_slug`. `form_variant` is the per-instance discriminator that makes
 * hero-vs-bottom attributable in `interactions`. Omitted entirely when the
 * caller has only one form on the page, so existing rows stay comparable.
 */
function withFormVariant(
  data: Record<string, unknown>,
  formVariant?: string,
): Record<string, unknown> {
  return formVariant ? { ...data, form_variant: formVariant } : data;
}

export function trackFormStart(formVariant?: string): void {
  formStartTs = Date.now();
  queueEvent("form_start", withFormVariant({}, formVariant));
  // Also a Metrika goal so it can join engaged_30s in the composite Direct proxy.
  reachGoal("form_start", withFormVariant({}, formVariant));
}

export function trackFormFieldFocus(field: string, formVariant?: string): void {
  queueEvent("form_field_focus", withFormVariant({ field }, formVariant));
}

export function trackFormFieldError(field: string, errorType: string, formVariant?: string): void {
  queueEvent("form_field_error", withFormVariant({ field, error_type: errorType }, formVariant));
}

export function trackFormAbandon(
  fields: {
    name_filled: boolean;
    email_filled: boolean;
    consents_checked: number;
    consents_total: number;
  },
  formVariant?: string,
): void {
  queueEvent("form_abandon", withFormVariant({ ...fields }, formVariant));
  // Fired on pagehide/visibilitychange-hidden, so it must ship immediately via
  // sendBeacon — a queued-only event would be lost on unload (same reason
  // trackRegistrationSubmit flushes now).
  flushNow();
}

export function trackFormSubmit(offerId: string, tier: string): void {
  const duration = formStartTs > 0 ? Date.now() - formStartTs : undefined;
  queueEvent("form_submit", { offer_id: offerId, tier, duration_ms: duration });
  reachGoal("form_submit", { tier, offer_id: offerId });
  checkoutOpenTs = 0;
  // Flush immediately: the checkout form redirects to the payment provider on
  // success, so a queued form_submit would be lost before the 2s batch timer.
  flushNow();
}

export function trackCheckoutClose(): void {
  if (checkoutOpenTs > 0) {
    queueEvent("checkout_close", {
      duration_ms: Date.now() - checkoutOpenTs,
    });
    checkoutOpenTs = 0;
  }
}

export function trackRegistrationSubmit(eventSlug: string, formVariant?: string): void {
  queueEvent("registration_submit", withFormVariant({ event_slug: eventSlug }, formVariant));
  reachGoal("registration_submit", withFormVariant({ event_slug: eventSlug }, formVariant));
  // Flush immediately: lead-magnet/webinar forms redirect to the thank-you /
  // check-your-inbox page on success, so a queued event would be lost before the
  // 2s batch timer. This is why registration_submit never reached the collector.
  flushNow();
}

/**
 * Post-submit "what's next" page landing (lead-magnet thank-you /
 * check-your-inbox). Records that a visitor reached the next-steps page, tagged
 * with the lead-magnet `source` they came from and the page `mode`
 * (confirm|delivered), so upsell-card clicks (fired as `section_interact` via
 * `data-track` on each card) can be attributed to the originating funnel by
 * `session_id`. Uses the allowlisted `section_interact` type — no collector
 * change. Flushed immediately: the visitor may click a card (navigating away)
 * before the 2s batch timer, same reason `registration_submit` flushes now.
 */
export function trackLeadMagnetNext(source: string, mode: string): void {
  queueEvent("section_interact", {
    target: `lm_next.view.${mode}`,
    section_name: "lead-magnet-next-steps",
    section_role: "closer",
    ...(source ? { source } : {}),
    mode,
  });
  flushNow();
}

export function trackBotConnectInterestView(): void {
  queueEvent("section_interact", {
    target: "lm_next.view.bot_connect_interest",
    section_name: "lead-magnet-next-steps",
    section_role: "closer",
  });
  flushNow();
}

/**
 * Native website quiz (ADR-0093) — progressive, `visitor_id`-keyed first-party
 * capture for EVERYONE who touches the quiz, incl. abandoners and
 * non-registrants. The four `quiz_*` event types are pre-declared in the shared
 * `EVENT_TYPES` allowlist. PII rule (hard): only non-PII answers may be passed
 * here — email/name NEVER enter the anonymous event store (they live only on the
 * identified opt_ins/deal side). `quiz_start`/`quiz_answer` ride the 2s batch +
 * the pagehide/visibilitychange `flushNow()` beacon (so an abandoned quiz still
 * lands its answered steps); `quiz_complete`/`quiz_cta_click` flush immediately.
 */
export function trackQuizStart(quizId: string): void {
  queueEvent("quiz_start", { quiz_id: quizId });
}

export function trackQuizAnswer(payload: {
  quizId: string;
  questionId: string;
  answer: string | string[];
  stepIndex: number;
  archetypeSoFar?: string;
}): void {
  queueEvent("quiz_answer", {
    quiz_id: payload.quizId,
    question_id: payload.questionId,
    answer: payload.answer,
    step_index: payload.stepIndex,
    ...(payload.archetypeSoFar ? { archetype_so_far: payload.archetypeSoFar } : {}),
  });
}

export function trackQuizAnswerImmediately(payload: Parameters<typeof trackQuizAnswer>[0]): void {
  trackQuizAnswer(payload);
  flushNow();
}

export function trackQualificationComplete(payload: {
  quizId: string;
  answers: Record<string, string>;
}): void {
  queueEvent("quiz_complete", {
    quiz_id: payload.quizId,
    answers: payload.answers,
  });
  flushNow();
}

export function trackQuizComplete(payload: {
  quizId: string;
  symptom: string;
  cluster?: string;
}): void {
  queueEvent("quiz_complete", {
    quiz_id: payload.quizId,
    symptom: payload.symptom,
    ...(payload.cluster ? { cluster: payload.cluster } : {}),
  });
  // Fired at the email-gate submit (registration) — flush now so it isn't lost
  // if the tab is backgrounded before the 2s batch timer.
  flushNow();
}

export function trackQuizCtaClick(payload: {
  quizId: string;
  symptom: string;
  target: string;
}): void {
  queueEvent("quiz_cta_click", {
    quiz_id: payload.quizId,
    symptom: payload.symptom,
    target: payload.target,
  });
  // The result CTA may navigate away, so ship immediately.
  flushNow();
}

export function trackCheckoutError(tier: string, error: string): void {
  queueError({
    error_type: "checkout_error",
    message: error,
    page_url: location.href,
    metadata: { tier },
  });
}
